/**
 * UsersMain
 *
 * Main component for handling user routes, including user list, view, and edit.
 *
 * - Redirects to logout if user is not logged in.
 * - Displays permission warning if user lacks access to the "users" resource.
 * - Loads individual user data via PageLoader when accessing edit or view routes.
 *
 * @returns {JSX.Element} Users routes and components based on current URL.
 */
import React, { useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import { Routes, Route, useNavigate, useParams } from 'react-router-dom'

import { useStores } from '../../../cls/stores/MobXProvider'
import { PageLoader } from '../../../components/Shared'
import { actLoadUser } from '../../../stores/users/actions'
import UserEdit from './UserEdit'
import UsersLst from './UsersLst'
import { useSmartTranslation } from '../../../cls/TranslationContext'

const UsersMain = () => {
	const { t } = useSmartTranslation()
	const navigate = useNavigate()
	const { users } = useStores()
	const { itmId } = useParams()
	const { loggedInUserId, loggedInUser } = users
	const isAdmin = loggedInUser?.isAdmin
	const isSuperAdmin = loggedInUser?.isSuperAdmin
	useEffect(() => {
		if (!users.isLoggedIn) {
			navigate('/logout')
			return
		}
	}, [loggedInUserId, itmId])

	// If the user does not have personal permission
	// and lacks the required permissions for the action and resource
	// Display a message indicating lack of permissions
	if (!(isAdmin || isSuperAdmin || users.canUser('read', 'users'))) {
		return (
			<div style={{ textAlign: 'center' }}>
				{t('__denied.access').charAt(0).toUpperCase() +
					t('__denied.access%__feature-off%_user')
						.toLowerCase()
						.slice(1)}
			</div>
		)
	}

	// we have a custom logic for curent user profile, user navigates to /users/profile/edit
	// Should be redirected to /users/:itmId/edit based on the current loggedInUserId

	return (
		<Routes>
			<Route
				path=":itmId/*"
				element={
					<PageLoader
						forceLoad={true}
						requireItm={(id) => {
							return users.requireUserById(id)
						}}
						loadItm={(id) => actLoadUser(users, id, true)}
						targetLst="users"
					/>
				}
			>
				<Route path="edit" element={<UserEdit />} />
				<Route path="view" element={<UserEdit viewOnly={true} />} />
			</Route>
			<Route index element={<UsersLst />} />
			<Route path="*" element={<div>{t('single.not-found')}</div>} />
		</Routes>
	)
}
export default observer(UsersMain)
